import { z } from 'zod';

import { ZodCurrency } from '@Common/types/Amount';
import { ZodMdlrAccountIntent } from '@Common/types/bo/transfers';
import { ZodDate } from '@Common/types/date';
import { ZodId } from '@Common/types/request';
import { ZodSuccessResponse } from '@Common/types/response';
import { endpoint, GetEndpointType } from '@Common/types/zod/zod-endpoint';
import { createZodParamsValidator } from '@Common/types/zod/zod-params';
import { ZodTrimmedString } from '@Common/types/zod/zod-trimmed-string';

const ZodQaMdlrDeleteAccount = z.object({
  intent: ZodMdlrAccountIntent,
  userId: ZodId,
});

const ZodQaMdlrDeleteAccountResponse = z.object({
  count: z.number(),
});

export const postQaMdlrDeleteAccountEndpoint = endpoint({
  method: 'POST',
  body: ZodQaMdlrDeleteAccount,
  responseBody: ZodQaMdlrDeleteAccountResponse,
});

export type PostQaMdlrDeleteAccountEndpoint = GetEndpointType<
  typeof postQaMdlrDeleteAccountEndpoint
>;

const MdlrAccountStatusValues = [
  'active',
  'inactive',
  'blocked',
  'closed',
  'client_blocked',
] as const;

export const MdlrAccountTypeValues = ['scan', 'iban', 'dd', 'intl'] as const;

export const MdlrAccountsLegalEntityValues = ['GB', 'IE', 'FR'] as const;

export const MdlrAccountsSharesEntityValues = [
  'SAL',
  'SDA',
  'SDAP',
  'SFA',
  'SFL',
  'SHARES_SAVINGS',
] as const;

const ZodQaModulrAccountsGetMdlrSavingsAccountResponse = z.object({
  id: z.number().int(),
  mdlr_id: ZodTrimmedString.nullable(),
  status: z.enum(MdlrAccountStatusValues).nullable(),
  acc_number: ZodTrimmedString.nullable(),
  acc_type: z.enum(MdlrAccountTypeValues).nullable(),
  acc_sort_code: ZodTrimmedString.nullable(),
  acc_iban: ZodTrimmedString.nullable(),
  acc_bic: ZodTrimmedString.nullable(),
  currency: ZodCurrency,
  user_id: z.number().int().nullable(),
  created_at: ZodDate,
  updated_at: ZodDate,
  intent: z.enum(['user_savings']),
  legal_entity: z.enum(MdlrAccountsLegalEntityValues),
  shares_entity: z.enum(MdlrAccountsSharesEntityValues).nullable(),
  b2b_company_id: z.number().int().nullable(),
  reference: ZodTrimmedString.nullable(),
  payment_provider: z.enum(['modulr', 'jpmorgan', 'treezor']),
});

export const getQaModulrAccountsGetMdlrSavingsAccountEndpoint = endpoint({
  method: 'GET',
  responseBody: ZodQaModulrAccountsGetMdlrSavingsAccountResponse.nullable(),
});

export type GetQaModulrAccountsGetMdlrSavingsAccountEndpoint = GetEndpointType<
  typeof getQaModulrAccountsGetMdlrSavingsAccountEndpoint
>;

export const ZodPostModulrAccountCreateAccountForCompanyCompanyIdParams = createZodParamsValidator(
  z.object({
    company_id: z.number(),
  }),
);

export const postQaModulrCompleteMdlrTransferEndpoint = endpoint({
  method: 'POST',
  body: z.object({
    modulr_payment_id: z.number(),
  }),
  responseBody: ZodSuccessResponse,
});

export type PostQaModulrCompleteMdlrTransferEndpoint = GetEndpointType<
  typeof postQaModulrCompleteMdlrTransferEndpoint
>;

export const getQaModulrWithdrawalsEndpoint = endpoint({
  method: 'GET',
  responseBody: z
    .object({
      payment_id: z.number().int(),
      user_id: z.number().int(),
      created_at: z.date(),
      updated_at: z.date(),
      is_settled_automatically: z.boolean(),
    })
    .array(),
});

export type GetQaModulrWithdrawalsEndpoint = GetEndpointType<typeof getQaModulrWithdrawalsEndpoint>;

export const putQaModulrWithdrawalExecuteEndpoint = endpoint({
  method: 'PUT',
  pathParams: createZodParamsValidator(
    z.object({
      payment_id: z.number().int(),
    }),
  ),
  responseBody: ZodSuccessResponse,
});

export type PutQaModulrWithdrawalExecuteEndpoint = GetEndpointType<
  typeof putQaModulrWithdrawalExecuteEndpoint
>;
